import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Clock, 
  MapPin, 
  CheckCircle2, 
  XCircle,
  LogIn,
  LogOut,
  Coffee,
  Play,
  Delete,
  Building2,
  AlertCircle,
  Server
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Isolated clock component to prevent re-renders of the entire Kiosk
const KioskClock = React.memo(({ timezone, language }) => {
  const [displayTime, setDisplayTime] = useState('--:--:--');
  const [displayDate, setDisplayDate] = useState('...');
  const baseTimeRef = useRef(null);
  const offsetRef = useRef(0);

  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        const response = await axios.get(`${API_URL}/server-time`);
        const serverTimestamp = response.data.timestamp * 1000;
        const localNow = Date.now();
        offsetRef.current = serverTimestamp - localNow;
        baseTimeRef.current = serverTimestamp;
      } catch (error) {
        console.error('Error fetching server time:', error);
      }
    };

    fetchServerTime();
    const syncInterval = setInterval(fetchServerTime, 30000);

    const tickInterval = setInterval(() => {
      const now = Date.now() + offsetRef.current;
      const date = new Date(now);
      const tz = timezone || 'Europe/Madrid';
      
      const timeStr = date.toLocaleTimeString('es-ES', { 
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const dateStr = date.toLocaleDateString('es-ES', { 
        timeZone: tz,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      setDisplayTime(timeStr);
      setDisplayDate(dateStr);
    }, 1000);

    return () => {
      clearInterval(syncInterval);
      clearInterval(tickInterval);
    };
  }, [timezone]);

  return (
    <>
      <div className="flex items-center justify-center gap-2 mb-2">
        <Server className="w-5 h-5 text-green-500" />
        <span className="text-xs text-slate-500 font-medium">
          {language === 'es' ? 'Hora del servidor' : 'Server time'}
          {timezone && ` (${timezone.split('/').pop()})`}
        </span>
      </div>
      <div className="time-display text-slate-900 tabular-nums" data-testid="kiosk-server-time">
        {displayTime}
      </div>
      <p className="text-slate-500 mt-1">{displayDate}</p>
    </>
  );
});

KioskClock.displayName = 'KioskClock';

const Kiosk = () => {
  const { orgSlug } = useParams();
  const { t, language, toggleLanguage } = useLanguage();
  const [pin, setPin] = useState('');
  const [step, setStep] = useState('pin');
  const [employee, setEmployee] = useState(null);
  const [message, setMessage] = useState('');
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [orgErrorReason, setOrgErrorReason] = useState('not_found');
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Regulisto',
    company_logo: null
  });

  // NOTE: Do NOT clear localStorage here — the kiosk uses only public/PIN endpoints
  // and does not need an admin session. Clearing localStorage fires storage events
  // in other open tabs (admin panel) and causes their CompanyContext to reset.

  // Fetch organization info if org slug is provided
  useEffect(() => {
    const fetchOrgInfo = async () => {
      if (orgSlug) {
        try {
          const response = await axios.get(`${API_URL}/kiosk/${orgSlug}/info`);
          setOrgInfo(response.data);
          const orgName = response.data.company_name || response.data.name || 'Regulisto';
          setCompanySettings({
            company_name: orgName,
            company_logo: response.data.company_logo
          });
          document.title = orgName;
        } catch (error) {
          console.error('Organization not found:', error);
          setOrgErrorReason(error.response?.status === 403 ? 'disabled' : 'not_found');
          setStep('org_error');
        }
      } else {
        // No org slug - fetch global settings
        try {
          const response = await axios.get(`${API_URL}/settings/public`);
          setCompanySettings(response.data);
        } catch (error) {
          console.log('Could not fetch company settings');
        }
      }
    };
    fetchOrgInfo();
  }, [orgSlug]);

  // Get geolocation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        () => setLocationError(true),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const handlePinInput = (digit) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
  };

  const verifyPin = useCallback(async () => {
    if (pin.length < 4) return;
    
    setLoading(true);
    try {
      // Use org-specific endpoint if org slug is provided
      const endpoint = orgSlug 
        ? `${API_URL}/kiosk/${orgSlug}/employee/${pin}`
        : `${API_URL}/kiosk/employee/${pin}`;
      
      const response = await axios.get(endpoint);
      setEmployee(response.data);
      setStep('action');
    } catch (error) {
      const isRateLimited = error.response?.status === 429;
      const errorMsg = isRateLimited
        ? (error.response?.data?.detail || (language === 'es' ? 'Demasiados intentos. Espera un momento.' : 'Too many attempts. Please wait a moment.'))
        : orgSlug
          ? (language === 'es' ? 'PIN no válido para esta empresa' : 'Invalid PIN for this organization')
          : t('invalid_pin');
      setMessage(errorMsg);
      setStep('error');
      setTimeout(() => {
        setStep('pin');
        setPin('');
        setMessage('');
      }, isRateLimited ? 4000 : 2000);
    } finally {
      setLoading(false);
    }
  }, [pin, orgSlug, t, language]);

  useEffect(() => {
    if (pin.length === 4 && step === 'pin') {
      verifyPin();
    }
  }, [pin, verifyPin, step]);

  const handleClock = async (recordType) => {
    setLoading(true);
    try {
      // Use org-specific endpoint if org slug is provided
      const endpoint = orgSlug 
        ? `${API_URL}/kiosk/${orgSlug}/clock`
        : `${API_URL}/kiosk/clock`;
      
      // Server time is used for legal compliance - no client timestamp needed
      const response = await axios.post(endpoint, {
        pin: pin,
        record_type: recordType,
        location: location
      });
      
      setMessage(
        `${response.data.employee_name} - ${getRecordTypeLabel(recordType)}\n${formatTime(response.data.timestamp)}`
      );
      setStep('success');
    } catch (error) {
      setMessage(error.response?.data?.detail || t('error_occurred'));
      setStep('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setStep('pin');
        setPin('');
        setEmployee(null);
        setMessage('');
      }, 3000);
    }
  };

  const getRecordTypeLabel = (type) => {
    const labels = {
      clock_in: t('clock_in'),
      clock_out: t('clock_out'),
      break_start: t('break_start'),
      break_end: t('break_end')
    };
    return labels[type] || type;
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '--:--:--';
    }
  };

  const NumpadButton = ({ value, onClick, className = '', children }) => (
    <button
      onClick={onClick}
      className={`w-20 h-20 text-2xl font-semibold rounded-xl bg-white border-2 border-slate-200 
        hover:border-indigo-400 hover:bg-indigo-50 transition-all active:scale-95 
        flex items-center justify-center ${className}`}
      data-testid={`numpad-${value}`}
    >
      {children || value}
    </button>
  );

  const ActionButton = ({ type, icon: Icon, label, color, onClick }) => (
    <Button
      onClick={onClick}
      disabled={loading}
      className={`h-24 w-full text-lg font-medium ${color} transition-all active:scale-95`}
      data-testid={`kiosk-${type}`}
    >
      <Icon className="w-6 h-6 mr-3" />
      {label}
    </Button>
  );

  // Organization not found error
  if (step === 'org_error') {
    return (
      <div 
        className="kiosk-container kiosk-mode flex items-center justify-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1541415534056-fad380cd68a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHdoaXRlJTIwZ2VvbWV0cmljJTIwcGFwZXIlMjB0ZXh0dXJlfGVufDB8fHx8fDE3NjYzMzU0MDV8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="flex flex-col items-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {orgErrorReason === 'disabled'
                ? (language === 'es' ? 'Kiosko no disponible' : 'Kiosk not available')
                : (language === 'es' ? 'Empresa no encontrada' : 'Organization not found')}
            </h2>
            <p className="text-slate-500 text-center mb-6">
              {orgErrorReason === 'disabled'
                ? (language === 'es'
                    ? 'El fichaje por kiosko no está activado para esta empresa.'
                    : 'Kiosk clock-in is not enabled for this company.')
                : (language === 'es'
                    ? `No se encontró ninguna empresa con el identificador "${orgSlug}"`
                    : `No organization found with identifier "${orgSlug}"`)}
            </p>
            <p className="text-sm text-slate-400">
              {language === 'es'
                ? 'Contacte con su administrador para obtener el enlace correcto.'
                : 'Contact your administrator for the correct link.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="kiosk-container kiosk-mode"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1541415534056-fad380cd68a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHdoaXRlJTIwZ2VvbWV0cmljJTIwcGFwZXIlMjB0ZXh0dXJlfGVufDB8fHx8fDE3NjYzMzU0MDV8MA&ixlib=rb-4.1.0&q=85')`
      }}
      data-testid="kiosk-page"
    >
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 p-4 flex items-center justify-between bg-white/80 backdrop-blur-md border-b border-slate-200/50">
        <div className="flex items-center gap-2">
          {/* Company logo and name */}
          {companySettings.company_logo ? (
            <img src={companySettings.company_logo} alt="Logo" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="font-semibold text-slate-900">{companySettings.company_name}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Organization badge */}
          {orgInfo && (
            <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-full">
              <Building2 className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">{orgInfo.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <MapPin className={`w-4 h-4 ${location ? 'text-emerald-500' : 'text-amber-500'}`} />
            <span className={`text-sm ${location ? 'text-emerald-600' : 'text-amber-600'}`}>
              {location ? t('location_valid') : (locationError ? t('location_invalid') : '...')}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={toggleLanguage}>
            {language === 'es' ? 'EN' : 'ES'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Card className="kiosk-card mt-16 animate-fade-in">
        <CardContent className="p-8">
          {/* Clock Display */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              {companySettings.company_logo ? (
                <img 
                  src={companySettings.company_logo} 
                  alt="Logo" 
                  className="w-12 h-12 object-contain rounded-xl"
                />
              ) : (
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-2xl font-bold font-['Manrope'] text-slate-900">
                {companySettings.company_name || 'Regulisto'}
              </span>
            </div>
            
            {/* Server Time Display - Isolated component */}
            <KioskClock 
              timezone={orgInfo?.timezone || companySettings?.timezone || 'Europe/Madrid'} 
              language={language} 
            />
          </div>

          {/* PIN Entry Step */}
          {step === 'pin' && (
            <div className="animate-fade-in">
              <p className="text-center text-slate-600 mb-4">{t('enter_pin')}</p>
              
              {/* PIN Display */}
              <div className="kiosk-pin-display">
                {pin.padEnd(4, '•').split('').map((char, i) => (
                  <span key={i} className={char !== '•' ? 'text-indigo-600' : 'text-slate-300'}>
                    {char === '•' ? '○' : '●'}
                  </span>
                ))}
              </div>

              {/* Numpad */}
              <div className="grid grid-cols-3 gap-3 justify-items-center">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                  <NumpadButton 
                    key={num} 
                    value={num} 
                    onClick={() => handlePinInput(String(num))}
                  />
                ))}
                <NumpadButton value="clear" onClick={handleClear} className="text-slate-400">
                  C
                </NumpadButton>
                <NumpadButton value="0" onClick={() => handlePinInput('0')} />
                <NumpadButton value="delete" onClick={handleDelete} className="text-slate-400">
                  <Delete className="w-6 h-6" />
                </NumpadButton>
              </div>
            </div>
          )}

          {/* Action Selection Step */}
          {step === 'action' && employee && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-indigo-700">
                    {employee.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-slate-900">{employee.name}</h2>
                <p className="text-slate-500">{t('clock_action')}</p>
              </div>

              {/* Today's records */}
              {employee.today_records?.length > 0 && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                    {language === 'es' ? 'Hoy' : 'Today'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {employee.today_records.map((record, i) => (
                      <span key={i} className="text-sm bg-white px-2 py-1 rounded border border-slate-200">
                        {getRecordTypeLabel(record.record_type)} - {formatTime(record.timestamp)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <ActionButton 
                  type="clock_in"
                  icon={LogIn}
                  label={t('clock_in')}
                  color="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleClock('clock_in')}
                />
                <ActionButton 
                  type="clock_out"
                  icon={LogOut}
                  label={t('clock_out')}
                  color="bg-red-600 hover:bg-red-700"
                  onClick={() => handleClock('clock_out')}
                />
                <ActionButton 
                  type="break_start"
                  icon={Coffee}
                  label={t('break_start')}
                  color="bg-amber-600 hover:bg-amber-700"
                  onClick={() => handleClock('break_start')}
                />
                <ActionButton 
                  type="break_end"
                  icon={Play}
                  label={t('break_end')}
                  color="bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleClock('break_end')}
                />
              </div>

              <Button 
                variant="ghost" 
                className="w-full mt-4"
                onClick={() => { setStep('pin'); setPin(''); setEmployee(null); }}
                data-testid="kiosk-cancel"
              >
                {t('cancel')}
              </Button>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="animate-fade-in text-center py-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-emerald-700 mb-2">{t('success')}</h2>
              <p className="text-slate-600 whitespace-pre-line">{message}</p>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="animate-fade-in text-center py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-red-700 mb-2">{t('error')}</h2>
              <p className="text-slate-600">{message}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
              <div className="spinner w-8 h-8 border-indigo-600" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Kiosk;
